/**
 * Time formatting utilities for converting military time to standard 12-hour format
 */

/**
 * Converts military time (24-hour format) to standard time (12-hour format with AM/PM)
 * 
 * @param time - Time string in various formats (e.g., "16:00", "16:00:00", "4:00 PM")
 * @returns Formatted time string in 12-hour format (e.g., "4:00 PM")
 * 
 * @example
 * formatTimeToStandard("16:00") // Returns "4:00 PM"
 * formatTimeToStandard("09:30") // Returns "9:30 AM"
 * formatTimeToStandard("3:00 PM") // Returns "3:00 PM" (already formatted)
 * formatTimeToStandard("") // Returns ""
 */
export function formatTimeToStandard(time: string | null | undefined): string {
  if (!time) return '';
  
  // Trim any whitespace
  const trimmedTime = time.trim();
  
  // Already in standard format (contains AM/PM)
  if (trimmedTime.match(/[ap]m/i)) {
    return trimmedTime;
  }
  
  // Parse military time (e.g., "16:00", "16:00:00")
  const match = trimmedTime.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return trimmedTime; // Return as-is if not parseable
  
  let hours = parseInt(match[1]);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  
  // Convert to 12-hour format
  if (hours === 0) {
    hours = 12;
  } else if (hours > 12) {
    hours -= 12;
  }
  
  return `${hours}:${minutes} ${period}`;
}
